import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'brass';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary shadow-key hover:bg-primary-strong',
  secondary: 'bg-surface text-ink border border-line-strong shadow-key-light hover:bg-surface-2',
  ghost: 'bg-transparent text-ink-2 hover:bg-surface-2',
  danger: 'bg-rose text-on-rose shadow-key hover:bg-rose-strong',
  success: 'bg-fern text-on-fern shadow-key hover:bg-fern-strong',
  brass: 'bg-brass text-on-brass shadow-key hover:brightness-105',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'min-h-10 px-3.5 text-sm rounded-md gap-1.5',
  md: 'min-h-11 px-4.5 text-[0.95rem] rounded-lg gap-2',
  lg: 'min-h-13 px-6 text-base rounded-xl gap-2.5',
};

/** Class names for button styling on non-button elements (e.g. router links). */
export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'md', block = false, className?: string): string {
  return cn(
    'inline-flex select-none items-center justify-center font-semibold transition-[transform,background-color,filter] duration-100',
    'active:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-55 disabled:active:translate-y-0',
    VARIANT[variant],
    SIZE[size],
    block && 'w-full',
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', block, loading, icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClass(variant, size, block, className)}
      {...rest}
    >
      {loading ? <Spinner className="size-4" /> : icon}
      {children}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: 'ghost' | 'secondary';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-full text-ink-2 transition-colors',
        variant === 'ghost' ? 'hover:bg-surface-2' : 'border border-line-strong bg-surface hover:bg-surface-2',
        'disabled:opacity-50',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Layout and display
// ---------------------------------------------------------------------------

export function Card({ className, as: As = 'div', ...rest }: HTMLAttributes<HTMLElement> & { as?: 'div' | 'section' | 'article' | 'li' }) {
  return <As className={cn('min-w-0 rounded-xl border border-line bg-surface shadow-card', className)} {...rest} />;
}

export type Tone = 'neutral' | 'primary' | 'brass' | 'fern' | 'rose' | 'sky';
const TONE: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-2',
  primary: 'bg-primary-soft text-primary-ink',
  brass: 'bg-brass-soft text-brass-ink',
  fern: 'bg-fern-soft text-fern-ink',
  rose: 'bg-rose-soft text-rose-ink',
  sky: 'bg-sky-soft text-sky-ink',
};

export function Pill({ tone = 'neutral', className, ...rest }: HTMLAttributes<HTMLSpanElement> & { tone?: Tone }) {
  return (
    <span
      className={cn('inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold', TONE[tone], className)}
      {...rest}
    />
  );
}

export function ProgressBar({
  value,
  max = 1,
  tone = 'primary',
  label,
  className,
  size = 'md',
}: {
  value: number;
  max?: number;
  tone?: 'primary' | 'brass' | 'fern';
  label: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const pct = max <= 0 ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  const fill = tone === 'primary' ? 'bg-primary' : tone === 'brass' ? 'bg-brass' : 'bg-fern';
  const height = size === 'sm' ? 'h-1.5' : size === 'lg' ? 'h-3.5' : 'h-2.5';
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.round(value * 100) / 100}
      className={cn('w-full overflow-hidden rounded-full bg-surface-2', height, className)}
    >
      <div className={cn('h-full rounded-full transition-[width] duration-500 ease-out', fill)} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function ProgressRing({ value, max = 1, size = 56, stroke = 6, label, children, tone = 'brass' }: {
  value: number;
  max?: number;
  size?: number;
  stroke?: number;
  label: string;
  children?: ReactNode;
  tone?: 'brass' | 'primary' | 'fern';
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = max <= 0 ? 0 : Math.max(0, Math.min(1, value / max));
  const color = tone === 'brass' ? 'var(--color-brass)' : tone === 'primary' ? 'var(--color-primary)' : 'var(--color-fern)';
  return (
    <div className="relative inline-grid place-items-center" style={{ width: size, height: size }} role="img" aria-label={label}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-surface-2)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">{children}</div>
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cn('animate-spin', className ?? 'size-5')} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden="true" className={cn('animate-pulse rounded-lg bg-surface-2', className)} />;
}

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="sr-only">{children}</span>;
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
  optional,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  htmlFor?: string;
  optional?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-semibold text-ink">
        {label}
        {optional && <span className="ml-1 font-normal text-muted">(optional)</span>}
      </label>
      {children}
      {hint && !error && <p className="text-sm text-muted">{hint}</p>}
      {error && (
        <p className="text-sm font-medium text-rose-ink" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const inputBase =
  'w-full min-h-11 rounded-lg border bg-surface px-3.5 text-base text-ink placeholder:text-muted/70 transition-colors ' +
  'border-line-strong hover:border-ink-2/40 focus:border-primary focus:outline-none focus-visible:outline-3 focus-visible:outline-primary/40 ' +
  'aria-[invalid=true]:border-rose disabled:bg-surface-2 disabled:text-muted';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cn(inputBase, className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(inputBase, 'min-h-24 py-2.5', className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return (
    <select ref={ref} className={cn(inputBase, 'appearance-none bg-[length:1rem] bg-[right_0.75rem_center] bg-no-repeat pr-9', className)} style={{ backgroundImage: CHEVRON }} {...rest}>
      {children}
    </select>
  );
});

const CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235b6275' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")";

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
  id,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
}) {
  const autoId = useId();
  const controlId = id ?? autoId;
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="min-w-0">
        <label htmlFor={controlId} className="font-semibold text-ink">
          {label}
        </label>
        {description && <p className="text-sm text-muted">{description}</p>}
      </div>
      <button
        id={controlId}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
          'before:absolute before:-inset-2 before:content-[""]',
          checked ? 'bg-primary' : 'bg-line-strong',
        )}
      >
        <span className={cn('inline-block size-5.5 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-5.5' : 'translate-x-0.75')} />
      </button>
    </div>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

/** Accessible radio group rendered as segmented buttons (arrow keys move selection). */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
  size = 'md',
}: {
  value: T;
  onChange: (value: T) => void;
  options: SegmentOption<T>[];
  label: string;
  className?: string;
  size?: 'md' | 'lg';
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const move = (from: number, dir: 1 | -1) => {
    const enabled = options.map((o, i) => (!o.disabled ? i : -1)).filter((i) => i >= 0);
    const pos = enabled.indexOf(from);
    const next = enabled[(pos + dir + enabled.length) % enabled.length];
    if (next === undefined) return;
    onChange(options[next]!.value);
    refs.current[next]?.focus();
  };
  return (
    <div role="radiogroup" aria-label={label} className={cn('inline-flex flex-wrap gap-1 rounded-xl bg-surface-2 p-1', className)}>
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                move(index, 1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                move(index, -1);
              }
            }}
            className={cn(
              'flex-1 whitespace-nowrap rounded-lg px-3 font-semibold transition-colors disabled:opacity-40',
              size === 'lg' ? 'min-h-12 text-base' : 'min-h-10 text-sm',
              selected ? 'bg-surface text-ink shadow-card' : 'text-muted hover:text-ink',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** Large selectable cards (radio semantics) for onboarding-style choices. */
export function ChoiceCards<T extends string>({
  value,
  onChange,
  options,
  label,
  multiple,
  columns = 2,
}: {
  value: T[];
  onChange: (value: T[]) => void;
  options: (SegmentOption<T> & { icon?: ReactNode })[];
  label: string;
  multiple?: boolean;
  columns?: 1 | 2 | 3;
}) {
  const grid = columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3';
  return (
    <div role={multiple ? 'group' : 'radiogroup'} aria-label={label} className={cn('grid gap-3', grid)}>
      {options.map((option) => {
        const selected = value.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            role={multiple ? 'checkbox' : 'radio'}
            aria-checked={selected}
            disabled={option.disabled}
            onClick={() => {
              if (multiple) onChange(selected ? value.filter((v) => v !== option.value) : [...value, option.value]);
              else onChange([option.value]);
            }}
            className={cn(
              'flex min-h-16 items-start gap-3 rounded-xl border-2 bg-surface p-4 text-left transition-colors',
              selected ? 'border-primary bg-primary-soft/60' : 'border-line hover:border-line-strong',
            )}
          >
            {option.icon && <span className="mt-0.5 text-primary">{option.icon}</span>}
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-ink">{option.label}</span>
              {option.description && <span className="mt-0.5 block text-sm text-muted">{option.description}</span>}
            </span>
            <span
              aria-hidden="true"
              className={cn(
                'mt-1 grid size-5 shrink-0 place-items-center border-2',
                multiple ? 'rounded-md' : 'rounded-full',
                selected ? 'border-primary bg-primary text-on-primary' : 'border-line-strong',
              )}
            >
              {selected && (
                <svg viewBox="0 0 16 16" className="size-3" fill="none" stroke="currentColor" strokeWidth="3">
                  <path d="m3.5 8.5 3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog (native <dialog>: focus trapping, Escape, inert background)
// ---------------------------------------------------------------------------

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  const width = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-lg';
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className={cn(
        'm-auto w-[calc(100%-2rem)] rounded-2xl border border-line bg-surface p-0 text-ink shadow-raised backdrop:bg-ink/40 backdrop:backdrop-blur-[2px]',
        'open:animate-pop max-h-[calc(100dvh-2rem)]',
        width,
      )}
    >
      {open && (
        <div className="flex max-h-[calc(100dvh-2rem)] flex-col">
          <div className="px-5 pt-5 sm:px-6 sm:pt-6">
            <h2 id={titleId} className="text-xl font-bold">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1.5 text-muted">
                {description}
              </p>
            )}
          </div>
          {children && <div className="min-h-0 overflow-y-auto px-5 py-4 sm:px-6">{children}</div>}
          {footer && (
            <div
              className={cn(
                'flex gap-2 border-t border-line px-5 py-4 sm:px-6',
                // Small dialogs stack full-width buttons, last (primary) on top, instead of wrapping unevenly.
                size === 'sm' ? 'flex-col-reverse *:w-full' : 'flex-wrap justify-end',
              )}
            >
              {footer}
            </div>
          )}
        </div>
      )}
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

export function Alert({ tone = 'sky', title, children, icon, action }: { tone?: Tone; title?: ReactNode; children?: ReactNode; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div role={tone === 'rose' ? 'alert' : 'status'} className={cn('flex items-start gap-3 rounded-lg p-3.5 text-sm', TONE[tone])}>
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(Boolean(title) && 'mt-0.5', 'opacity-95')}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function EmptyState({ icon, title, children, action, className }: { icon?: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center rounded-xl border border-dashed border-line-strong px-6 py-10 text-center', className)}>
      {icon && <div className="mb-3 grid size-12 place-items-center rounded-full bg-surface-2 text-muted">{icon}</div>}
      <h3 className="text-lg font-bold">{title}</h3>
      {children && <div className="mt-1 max-w-md text-muted">{children}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Stat({ label, value, sub, icon, tone = 'neutral' }: { label: ReactNode; value: ReactNode; sub?: ReactNode; icon?: ReactNode; tone?: Tone }) {
  return (
    <div className="flex items-center gap-3">
      {icon && <span className={cn('grid size-10 shrink-0 place-items-center rounded-lg', TONE[tone])}>{icon}</span>}
      <div className="min-w-0">
        <p className="text-sm text-muted">{label}</p>
        <p className="tabular font-display text-xl font-bold leading-tight">{value}</p>
        {sub && <p className="text-xs text-muted">{sub}</p>}
      </div>
    </div>
  );
}

/** Tabs with roving focus and ARIA tab semantics. */
export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
  label,
}: {
  value: T;
  onChange: (value: T) => void;
  tabs: { value: T; label: ReactNode; count?: number }[];
  label: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  return (
    // The divider is an inset shadow (not a border the tabs overlap), so the row never overflows vertically
    // and only scrolls sideways when the tabs don't fit.
    <div role="tablist" aria-label={label} className="-mx-1 flex gap-1 overflow-x-auto overflow-y-hidden px-1 shadow-[inset_0_-1px_0_var(--color-line)] [scrollbar-width:thin]">
      {tabs.map((tab, index) => {
        const selected = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            role="tab"
            type="button"
            id={`tab-${tab.value}`}
            aria-selected={selected}
            aria-controls={`panel-${tab.value}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.value)}
            onKeyDown={(e) => {
              const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
              if (!dir) return;
              e.preventDefault();
              const next = (index + dir + tabs.length) % tabs.length;
              onChange(tabs[next]!.value);
              refs.current[next]?.focus();
            }}
            className={cn(
              'flex min-h-11 shrink-0 items-center gap-2 border-b-3 px-3 text-sm font-semibold transition-colors',
              selected ? 'border-primary text-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {tab.label}
            {tab.count !== undefined && <span className="tabular rounded-full bg-surface-2 px-1.5 text-xs text-muted">{tab.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ value, children }: { value: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`panel-${value}`} aria-labelledby={`tab-${value}`} tabIndex={0} className="focus-visible:outline-none">
      {children}
    </div>
  );
}
